import { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Keyboard, A11y } from 'swiper';
import styled from 'styled-components';
import { useLocalization } from '../custom-hooks/useLocalization';
import { withLocalization } from '../shared/Context/LocalizationContext';
import EventWebinarCard from './cards/EventWebinarCard/EventWebinarCard';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import { device } from '../../utils/styled-component-mixins';
import BdoSelectInputContainer from '../shared/BdoSelectInput/BdoSelectInputContainer';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import CardSwiperWrapperStyled from './styled-components/CardSwiperWrapperStyled';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';
import { compose } from '../utils/compose';
import NoResultsCard from '../shared/NoResultsCard';
import useIsFirstRender from '../custom-hooks/use-is-first-render';
import getSelectedOptionKey from '../../utils/get-selected-option-key';

const EventsWebinarsSliderComponentStyled = styled.div`
  .swiper {
    .swiper-slide {
      max-width: auto;

      @media ${device.desktop} {
        width: 25%;
        max-width: 25%;
      }
    }
  }

  .event-webinar-card-wrapper {
    flex: 1;
  }

  .container-padding-md {
    padding-left: 0;
  }

  .heading-wrapper {
    margin-bottom: 3rem;

    h2 {
      font-size: 3rem;
      font-weight: normal;
      line-height: 3.2rem;
      padding: 0 1.5rem;
      position: relative;
      text-align: center;
      z-index: 1;

      @media ${device.desktop} {
        font-size: 3.6rem;
        line-height: 4.8rem;
      }
    }

    .sub-heading {
      margin: 1.6rem auto 0 auto;
      max-width: 70rem;
      padding: 0 1.5rem;
      position: relative;
      text-align: center;
      z-index: 1;

      @media ${device.desktop} {
        font-size: 2rem;
        line-height: 3.2rem;
      }
    }
  }
`;

const EventsWebinarsSliderComponent = (props) => {
  const { properties, eventsWebinars, hasFilter = true } = props;
  const { resourceStrings } = useLocalization();
  const dropDownLabel = resourceStrings['bdO.InsightsSectionWidget.FilterLabel'];
  const isDesktop = useMediaQuery(device.desktop);
  const noResultsMessage = resourceStrings['bdO.InsightSectionWidget.NoResults'];
  const isFirstRender = useIsFirstRender();
  // TODO: the initial value for the useState needs to be updated on Search is implemented
  // an initial call to the api will need to be made in EventsWebinarsSliderComponent to get the unfiltered events and webinars with
  // the Featured items included in the list.
  const [filteredCards, setFilteredCards] = useState(props.eventsWebinars);
  const [filterValue, setFilterValue] = useState(props.filterOptions[0]);
  const [featuredCardsNodeIds, setFeaturedCardsNodeIds] = useState([]);
  const filterLabel = resourceStrings['bdO.EventsWebinarsSliderWidget.FilterLabel'];
  const cardCtaText = resourceStrings['bdO.EventsWebinarsSliderWidget.CardCtaText'];
  const featuredText = resourceStrings['bdO.EventsAndWebinarsSliderWidget.FeaturedText'];
  const { selectedIndustry } = props;

  const dropDownMap = (categories, firstItem) => {
    const options = Object.keys(categories).map((key) => categories[key]);

    return [firstItem, ...options];
  };

  const getPreselectedFilter = () => {
    const filterKeys = Object.keys(props.filterOptions);
    let preselectedFilter = null;

    filterKeys.forEach((key, index) => {
      if (key?.toLowerCase() === selectedIndustry?.toLowerCase()) {
        // add 1 to index to account for the first item in the dropdown
        preselectedFilter = index + 1;
      }
    });

    return preselectedFilter;
  };

  const applyFilter = (cards, filter) => {
    const featuredCards = featuredCardsNodeIds.join(',');
    const filterKey =
      filter === dropDownLabel ? '' : getSelectedOptionKey(props.filterOptions, filter);

    fetch(
      `/api/events?industry=${filterKey}&take=25&excludeTotal=true&featured=${featuredCards}&notContentType=2`
    )
      .then((response) => response.json())
      .then((data) => {
        setFilteredCards(data);
      });
  };
  // TODO: update this useEffect once Search is implemented
  useEffect(() => {
    setFilteredCards(eventsWebinars);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isFirstRender) {
      applyFilter(props.items, filterValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);
  useEffect(() => {
    const nodeIds = props.featuredItems.map((item) => item.nodeId);
    setFeaturedCardsNodeIds(nodeIds);
  }, [props.featuredItems]);
  return (
    <WidgetIdWrapper widgetID={properties.widgetID}>
      <EventsWebinarsSliderComponentStyled>
        <div className="heading-wrapper">
          <h2>{properties.heading}</h2>
          <p className="sub-heading">{properties.subHeaderText}</p>
        </div>
        <div className="card-slider">
          {hasFilter && (
            <BdoSelectInputContainer
              className="bdo-select-container"
              filterLabel={filterLabel}
              filters={[
                {
                  filterOptions: dropDownMap(props.filterOptions, dropDownLabel),
                  onChange: setFilterValue,
                  preselectedOption: getPreselectedFilter()
                }
              ]}
            />
          )}

          <CardSwiperWrapperStyled
            backgroundImage={props.properties.backgroundImage}
            minHeight="65.5rem"
          >
            <div className="container-padding-md">
              <Swiper
                pagination={{ clickable: { isDesktop } }}
                centeredSlides={true}
                spaceBetween={16}
                modules={[Pagination, Navigation, Keyboard, A11y]}
                a11y={true}
                navigation={true}
                keyboard={true}
                breakpoints={{
                  320: {
                    slidesPerView: 'auto',
                    slidesPerGroup: 1
                  },
                  992: {
                    centeredSlides: false,
                    slidesPerView: 4,
                    slidesPerGroup: 4
                  }
                }}
              >
                {filteredCards.length > 0 ? (
                  filteredCards &&
                  filteredCards.map((eventWebinar) => (
                    <SwiperSlide key={eventWebinar.nodeId}>
                      <EventWebinarCard
                        key={eventWebinar.nodeId}
                        city={eventWebinar.city}
                        state={eventWebinar.state}
                        dateFrom={eventWebinar.dateFrom}
                        description={eventWebinar.description}
                        pageTitle={eventWebinar.pageTitle}
                        eventType={eventWebinar.eventType}
                        slug={eventWebinar.url}
                        cardCtaText={cardCtaText}
                        featuredText={featuredText}
                        isFeatured={eventWebinar.isFeatured}
                        timeZone={eventWebinar.timeZone}
                      />
                    </SwiperSlide>
                  ))
                ) : (
                  <NoResultsCard noResultsMessage={noResultsMessage} />
                )}
              </Swiper>
              {props.properties.ctaText && props.properties.ctaUrl && (
                <ButtonLink
                  text={props.properties.ctaText}
                  link={props.properties.ctaUrl}
                  target="_self"
                  type="tertiary"
                  theme="light"
                />
              )}
            </div>
          </CardSwiperWrapperStyled>
        </div>
      </EventsWebinarsSliderComponentStyled>
    </WidgetIdWrapper>
  );
};

export default compose(withLocalization, withWidgetSpacingWrapper)(EventsWebinarsSliderComponent);
